import { useState, useRef, useEffect, useCallback } from "react";
import { t, font } from "@realtyflows/ui";
import RioAvatar from "./RioAvatar";
import TypingDots from "./TypingDots";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

// Simple markdown: **bold**, *italic*, - lists, [links](url)
function renderMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:#2D5AA0;text-decoration:underline">$1</a>')
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/s, "<ul style='margin:4px 0;padding-left:16px'>$1</ul>")
    .replace(/\n/g, "<br/>");
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { const saved = localStorage.getItem("rio_public_messages"); return saved ? JSON.parse(saved) : []; } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem("rio_public_session") || null; } catch { return null; }
  });
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  // Save messages to localStorage
  useEffect(() => {
    try { localStorage.setItem("rio_public_messages", JSON.stringify(messages)); } catch {}
  }, [messages]);
  useEffect(() => {
    if (sessionId) try { localStorage.setItem("rio_public_session", sessionId); } catch {}
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: "Hi! I'm **Rio**, your RealtyFlows assistant. How can I help you today?", time: Date.now() }]);
      setUnread(0);
    }
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open, messages.length]);

  const sendMessage = useCallback(async (overrideMsg) => {
    const msg = overrideMsg || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg, time: Date.now() }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/chatbot/public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, message: msg }),
      });
      const data = await res.json();
      if (data.sessionId) setSessionId(data.sessionId);
      setMessages(prev => [...prev, { role: "assistant", content: data.message, time: Date.now() }]);
      if (!openRef.current) setUnread(u => u + 1);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "I'm offline right now. Email us at **support@realtyflows.com**", time: Date.now() }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId]);

  const suggestions = [
    { label: "Free CRM", msg: "What do I get with the free plan?" },
    { label: "Agent Network", msg: "How does the agent-to-agent network work?" },
    { label: "Pricing", msg: "What plans do you offer and how much do they cost?" },
    { label: "vs Others", msg: "How do you compare to Follow Up Boss and LionDesk?" },
  ];

  const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // Floating bubble
  if (!open) {
    return (
      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 999 }}>
        {/* Unread badge */}
        {unread > 0 && (
          <div style={{
            position: "absolute", top: -4, right: -4, width: 20, height: 20, borderRadius: "50%",
            background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1,
            border: "2px solid #fff",
          }}>{unread}</div>
        )}
        <button onClick={() => setOpen(true)} style={{
          width: 60, height: 60, borderRadius: "50%", border: "none", cursor: "pointer",
          background: "linear-gradient(135deg, #1A3260, #2D5AA0)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 24px rgba(26,50,96,0.35)",
          transition: "transform 0.3s, box-shadow 0.3s",
          animation: "chatPulse 3s ease-in-out infinite",
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; e.currentTarget.style.boxShadow = "0 6px 30px rgba(26,50,96,0.5)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(26,50,96,0.35)"; }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </button>
        <style>{`@keyframes chatPulse { 0%,100% { box-shadow: 0 4px 24px rgba(26,50,96,0.35); } 50% { box-shadow: 0 4px 30px rgba(26,50,96,0.5); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed", bottom: 90, right: 24, zIndex: 999,
      width: 360, maxHeight: "calc(100vh - 120px)", borderRadius: 20, overflow: "hidden",
      background: t.surface, boxShadow: "0 12px 48px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)",
      display: "flex", flexDirection: "column",
      animation: "chatSlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <style>{`@keyframes chatSlideUp { from { opacity:0; transform:translateY(20px) scale(0.95); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>

      {/* Header */}
      <div style={{
        padding: "16px 18px", display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "linear-gradient(135deg, #1A3260, #2D5AA0)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <RioAvatar size={36} animated />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", fontFamily: font.display }}>Rio</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", fontFamily: font.body, display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
              Online
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setMessages([]); setSessionId(null); try { localStorage.removeItem("rio_public_messages"); localStorage.removeItem("rio_public_session"); } catch {} }} title="New chat" style={{
            background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 28, height: 28,
            borderRadius: 6, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
          }}>&#8635;</button>
          <button onClick={() => setOpen(false)} style={{
            background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 28, height: 28,
            borderRadius: 6, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
          }}>&times;</button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "14px 14px 6px", background: "#f8f9fb" }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
            marginBottom: 14, gap: 8, alignItems: "flex-end",
          }}>
            {msg.role === "assistant" && <RioAvatar size={28} />}
            <div>
              <div style={{
                maxWidth: 260, padding: "10px 14px", borderRadius: 16,
                background: msg.role === "user" ? "linear-gradient(135deg, #1A3260, #2D5AA0)" : "#fff",
                color: msg.role === "user" ? "#fff" : t.textPrimary,
                fontSize: 13, fontFamily: font.body, lineHeight: 1.6,
                borderBottomRightRadius: msg.role === "user" ? 4 : 16,
                borderBottomLeftRadius: msg.role === "assistant" ? 4 : 16,
                boxShadow: msg.role === "assistant" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
              }} dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
              <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 3, textAlign: msg.role === "user" ? "right" : "left", fontFamily: font.mono }}>
                {msg.time ? formatTime(msg.time) : ""}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 14 }}>
            <RioAvatar size={28} speaking />
            <div style={{ padding: "10px 14px", borderRadius: 16, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <TypingDots />
            </div>
          </div>
        )}
        {/* Suggestions */}
        {messages.length <= 1 && !loading && (
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {suggestions.map(s => (
              <button key={s.label} onClick={() => sendMessage(s.msg)} style={{
                padding: "6px 12px", borderRadius: 20, border: `1px solid #e2e8f0`,
                background: "#fff", fontSize: 11, color: "#475569", cursor: "pointer",
                fontFamily: font.body, transition: "all 0.15s",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >{s.label}</button>
            ))}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "12px 14px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", gap: 8 }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); sendMessage(); } }}
          placeholder="Ask Rio anything..."
          disabled={loading}
          style={{
            flex: 1, padding: "10px 14px", borderRadius: 12, border: "1px solid #e2e8f0",
            background: "#f8f9fb", fontSize: 13, fontFamily: font.body, color: t.textPrimary, outline: "none",
            transition: "border-color 0.2s",
          }}
          onFocus={e => e.target.style.borderColor = "#2D5AA0"}
          onBlur={e => e.target.style.borderColor = "#e2e8f0"}
        />
        <button onClick={() => sendMessage()} disabled={loading || !input.trim()} style={{
          width: 40, height: 40, borderRadius: 12, border: "none",
          background: loading || !input.trim() ? "#e2e8f0" : "linear-gradient(135deg, #1A3260, #2D5AA0)",
          color: "#fff", cursor: loading ? "not-allowed" : "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>

      {/* Branding */}
      <div style={{ padding: "6px 14px", background: "#fff", borderTop: "1px solid #f1f5f9", textAlign: "center" }}>
        <span style={{ fontSize: 9, color: "#94a3b8", fontFamily: font.body }}>Powered by <strong>RealtyFlows AI</strong></span>
      </div>
    </div>
  );
}
